import React from 'react'
import styled from 'styled-components'
import {
  SectionPaper,
  SectionTitle,
  SubSectionTitle,
  SubSectionWrapper,
} from 'components/startup/startup-styles'
import { FundingInfo } from 'types/common'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { CHANNEL_TALK_URL } from 'infra/constants'
import { Column, Row } from 'components/common/layout'
import { checkEditorDefaultValue } from 'utils/validations'
import { TextViewer } from 'components/common/text-editor'
import { useIsDesktop } from 'utils/hooks'

function formatAmount(amount: number) {
  // amount가 10이면 10억, 10.5면 10억 5000만원 등으로 표기
  if (amount === 0) return '비공개'
  if (amount > 0) {
    const billion = Math.floor(amount)
    const million = Math.round((amount - billion) * 10000)
    if (million === 0) return `${billion}억`
    return `${billion}억 ${million}만원`
  }
  return '비공개'
}

function formatDate(date: string | null) {
  if (!date) return ''
  // Expecting YYYY-MM-DD or YYYY-MM
  const [year, month] = date.split('-')
  return `${year}.${month}`
}

export const StartupFundingInfo: React.FC<{
  fundings: FundingInfo[]
  reasonForInvesting: string | null
}> = ({ fundings, reasonForInvesting }) => {
  // attractedAt 내림차순, null은 마지막
  const isDesktop = useIsDesktop()

  const sorted = [...fundings].sort((a, b) => {
    if (!a.attractedAt) return 1
    if (!b.attractedAt) return -1
    return b.attractedAt.localeCompare(a.attractedAt)
  })

  return (
    <Column id='fundingInfo'>
      <Row style={{ justifyContent: 'space-between', alignItems: 'start' }}>
        <SectionTitle>투자 정보</SectionTitle>
        {fundings && fundings.length > 0 && (
          <Button
            variant={BtnType.SUPER_LOW_STRONG}
            style={{ width: 'fit-content' }}
            size={BtnSize.SMALL}
            onClick={() => {
              window.open(CHANNEL_TALK_URL, '_blank')
            }}
          >
            {isDesktop ? '투자정보' : ''} 오류 신고
          </Button>
        )}
      </Row>
      <Column style={{ gap: 32 }}>
        {fundings && fundings.length > 0 && (
          <FundingTable>
            <thead>
              <tr>
                <th>날짜</th>
                <th>투자단계</th>
                <th>투자 유치액</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((f) => (
                <tr key={f.id}>
                  <td>{f.attractedAt ? formatDate(f.attractedAt) : '-'}</td>
                  <td>{f.round}</td>
                  <td>{f.amount ? formatAmount(f.amount) : '비공개'}</td>
                </tr>
              ))}
            </tbody>
          </FundingTable>
        )}
        {reasonForInvesting && !checkEditorDefaultValue(reasonForInvesting) && (
          <SubSectionWrapper>
            <SubSectionTitle>Q. 투자는 어떻게 받으셨나요?</SubSectionTitle>
            <SectionPaper>
              <TextViewer value={reasonForInvesting} />
            </SectionPaper>
          </SubSectionWrapper>
        )}
      </Column>
    </Column>
  )
}

const FundingTable = styled.table`
  width: 100%;
  padding: 0 8px;
  th {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.color.line};
    font-weight: ${(p) => p.theme.fontWeight.regular};
    background-color: ${(p) => p.theme.color.background};
    @media ${(p) => p.theme.deviceSize.mobile} {
      font-size: 13px;
    }
  }
  td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.color.line};
    font-weight: ${(p) => p.theme.fontWeight.regular};
    @media ${(p) => p.theme.deviceSize.mobile} {
      font-size: 13px;
    }
  }
  tr:first-child td {
    font-weight: ${(p) => p.theme.fontWeight.semiBold};
  }
`
